import type { FC, ReactNode } from 'react'

import { Accessibility, GraduationCap, Laptop, Smartphone, Tablet } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { CountUp } from '@/components/landing/count-up'
import { Accent, SectionHeading } from '@/components/landing/section-heading'

import { stats } from '@/constants/landing'

const buildPath = (curve: (t: number) => number, samples = 140): string => {
    let path = ''

    for (let index = 0; index <= samples; index++) {
        const t = index / samples
        const x = 28 + t * 500
        const y = curve(t)

        path += `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)} `
    }

    return path.trim()
}

const symplecticPath = buildPath(t => 128 + Math.sin(t * 64) * 3.2 * (1 - t * 0.25))
const eulerPath = buildPath(t => 128 - (Math.exp(t * 2.4) - 1) * 9.4 + Math.sin(t * 64) * 3.2)

const classroom = [
    { name: "Maya R.", progress: 92, flag: false },
    { name: "Leo T.", progress: 64, flag: true },
    { name: "Ines K.", progress: 81, flag: false }
]

export const PlatformSection: FC = (): ReactNode => {
    return (
        <section id='platform' className='scroll-mt-20 border-t border-hairline py-24 sm:py-32'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <Reveal>
                    <SectionHeading
                        index="04"
                        label="Platform"
                        title={<>Built like a <Accent>lab instrument.</Accent></>}
                        description="Under the friendly surface sits a physics engine that would pass a peer review, wrapped in tools that make teaching and learning faster."
                    />
                </Reveal>

                <div className='mt-14 grid gap-4 lg:grid-cols-6 lg:gap-5'>
                    <Reveal className='lg:col-span-4'>
                        <article className='flex h-full flex-col rounded-[28px] border border-border/80 bg-white p-6 elevate sm:p-8'>
                            <div className='flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
                                <div className='max-w-md'>
                                    <h3 className='text-xl font-semibold tracking-[-0.02em] text-ink'>
                                        Lab-grade engine
                                    </h3>

                                    <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                                        Symplectic integrators and a fixed 240 Hz timestep keep energy honest, even when you fast-forward through a thousand orbits.
                                    </p>
                                </div>

                                <span className='shrink-0 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground'>
                                    Fig. 4.1
                                </span>
                            </div>

                            <div className='mt-6 flex-1 rounded-2xl border border-hairline bg-white p-3 sm:p-4'>
                                <svg viewBox='0 0 560 200' className='h-auto w-full' role='img' aria-label="Chart comparing total energy over a thousand orbits: Inertia's integrator stays flat while a naive Euler integrator drifts upward">
                                    <g stroke='var(--hairline)'>
                                        {[40, 80, 120, 160].map(y => (
                                            <line key={y} x1='28' x2='528' y1={y} y2={y} />
                                        ))}
                                    </g>

                                    <line x1='28' x2='528' y1='176' y2='176' stroke='var(--border)' />
                                    <line x1='28' x2='28' y1='16' y2='176' stroke='var(--border)' />

                                    <path d={eulerPath} fill='none' stroke='var(--muted-foreground)' strokeOpacity='0.55' strokeWidth='1.6' strokeDasharray='4 4' />
                                    <path d={symplecticPath} fill='none' stroke='var(--brand)' strokeWidth='2.2' strokeLinecap='round' pathLength={1} className='draw-path' />

                                    <text x='34' y='30' fontSize='11' fill='var(--muted-foreground)' fontFamily='var(--font-mono)'>
                                        total energy
                                    </text>

                                    <text x='528' y='194' fontSize='11' textAnchor='end' fill='var(--muted-foreground)' fontFamily='var(--font-mono)'>
                                        1,000 orbits
                                    </text>

                                    <text x='520' y='44' fontSize='11' textAnchor='end' fill='var(--muted-foreground)' fontFamily='var(--font-mono)'>
                                        naive Euler drifts
                                    </text>

                                    <text x='520' y='150' fontSize='11' textAnchor='end' fill='var(--brand)' fontFamily='var(--font-mono)'>
                                        Inertia stays exact
                                    </text>
                                </svg>
                            </div>
                        </article>
                    </Reveal>

                    <Reveal className='lg:col-span-2' delay={100}>
                        <article className='flex h-full flex-col rounded-[28px] border border-border/80 bg-white p-6 elevate sm:p-8'>
                            <h3 className='text-xl font-semibold tracking-[-0.02em] text-ink'>
                                Equations that move
                            </h3>

                            <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                                Every symbol shares a color with the object it describes, so the formula and the motion read as one thing.
                            </p>

                            <div className='mt-6 flex flex-1 flex-col justify-center rounded-2xl border border-hairline p-5'>
                                <p className='font-serif text-[34px] leading-none text-ink'>
                                    <span className='italic'>E</span>
                                    <sub className='font-serif text-base italic'>k</sub> = ½ <span className='rounded-md bg-brand-soft px-1 italic text-brand'>m</span> <span className='rounded-md bg-signal-soft px-1 italic text-signal'>v</span>²
                                </p>

                                <div className='mt-5 flex items-center gap-3'>
                                    <span className='size-6 rounded-full bg-[radial-gradient(circle_at_32%_28%,#8d9bff,#2f49ff_60%,#1a2ab8)]' />
                                    <span className='h-px flex-1 bg-linear-to-r from-signal/70 to-signal/0' />
                                    <span className='font-mono text-[11px] text-signal'>v = 3.2 m/s</span>
                                </div>
                            </div>
                        </article>
                    </Reveal>

                    <Reveal className='lg:col-span-2'>
                        <article className='flex h-full flex-col rounded-[28px] border border-border/80 bg-white p-6 elevate sm:p-8'>
                            <div className='flex items-end gap-3 text-ink'>
                                <Laptop className='size-9' strokeWidth={1.25} />
                                <Tablet className='size-7' strokeWidth={1.25} />
                                <Smartphone className='size-6' strokeWidth={1.25} />
                            </div>

                            <h3 className='mt-6 text-xl font-semibold tracking-[-0.02em] text-ink'>
                                Any screen, zero installs
                            </h3>

                            <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                                Runs smoothly on phones, tablets and school Chromebooks. Nothing to download and nothing for IT to approve.
                            </p>
                        </article>
                    </Reveal>

                    <Reveal className='lg:col-span-2' delay={100}>
                        <article className='flex h-full flex-col rounded-[28px] border border-border/80 bg-white p-6 elevate sm:p-8'>
                            <div className='flex items-center justify-between'>
                                <GraduationCap className='size-6 text-ink' strokeWidth={1.5} />

                                <span className='rounded-full bg-signal-soft px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] text-signal'>
                                    1 needs help with vectors
                                </span>
                            </div>

                            <ul className='mt-5 flex flex-col gap-3'>
                                {classroom.map(student => (
                                    <li key={student.name} className='flex items-center gap-3 text-xs'>
                                        <span className='w-14 shrink-0 text-muted-foreground'>{student.name}</span>
                                        <span className='h-1.5 flex-1 overflow-hidden rounded-full bg-secondary'>
                                            <span
                                                className={student.flag ? 'block h-full rounded-full bg-signal' : 'block h-full rounded-full bg-brand'}
                                                style={{ width: `${student.progress}%` }}
                                            />
                                        </span>
                                        <span className='w-8 shrink-0 text-right font-mono text-ink'>{student.progress}%</span>
                                    </li>
                                ))}
                            </ul>

                            <h3 className='mt-6 text-xl font-semibold tracking-[-0.02em] text-ink'>
                                Built for classrooms
                            </h3>

                            <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                                Assign a simulation, set the starting conditions and see exactly where understanding breaks down.
                            </p>
                        </article>
                    </Reveal>

                    <Reveal className='lg:col-span-2' delay={200}>
                        <article className='flex h-full flex-col rounded-[28px] border border-border/80 bg-white p-6 elevate sm:p-8'>
                            <div className='flex items-center gap-2'>
                                <Accessibility className='size-6 text-ink' strokeWidth={1.5} />

                                <span className='ml-auto flex gap-1.5 font-mono text-xs text-ink'>
                                    {['Tab', '←', '→'].map(keyLabel => (
                                        <kbd key={keyLabel} className='min-w-8 rounded-lg border border-border bg-white px-2 py-1 text-center shadow-[0_2px_0_var(--border)]'>
                                            {keyLabel}
                                        </kbd>
                                    ))}
                                </span>
                            </div>

                            <h3 className='mt-6 text-xl font-semibold tracking-[-0.02em] text-ink'>
                                Accessible by default
                            </h3>

                            <p className='mt-2 text-[15px] leading-relaxed text-muted-foreground'>
                                Keyboard control for every slider, screen reader descriptions for every scene and captions on every guided tour.
                            </p>
                        </article>
                    </Reveal>
                </div>

                <Reveal className='mt-5'>
                    <dl className='grid grid-cols-2 overflow-hidden rounded-[28px] border border-border/80 bg-white elevate lg:grid-cols-4'>
                        {stats.map((stat, index) => (
                            <div
                                key={stat.label}
                                className={cn(
                                    'border-hairline p-6 sm:p-8',
                                    index % 2 === 1 && 'border-l',
                                    index >= 2 && 'border-t lg:border-t-0',
                                    index === 2 && 'lg:border-l'
                                )}
                            >
                                <dt className='sr-only'>{stat.label}</dt>
                                <dd className='font-heading text-[clamp(2.2rem,4vw,3.4rem)] font-semibold leading-none tracking-[-0.04em] text-ink'>
                                    <CountUp value={stat.value} suffix={stat.suffix} />
                                </dd>
                                <dd aria-hidden='true' className='mt-3 text-sm text-muted-foreground'>
                                    {stat.label}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </Reveal>
            </div>
        </section>
    )
}
